import {
    Box,
    Button,
    ButtonGroup,
    forwardRef,
    IconButton,
    Link,
    Radio,
    Text,
    Tooltip,
} from '@chakra-ui/react'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { CgSoftwareDownload } from 'react-icons/cg'
import { useNavigate } from 'react-router-dom'

import {
    useGetMergedJobSeekerQuery,
    useDeleteManualWatchListMutation,
} from '../../api'
import { useSelectedJobSeeker } from '../../hooks/useGetMergedJobseekerQuery'
import { getDisplayNameByLength } from '../../utils/displayNameByLength'
import { NewReactionBadge } from '../Notification/NewReactionBadge'

import { FacilitySFURLIcon, HearingIcon } from '@/assets/icon/index.jsx'
import { ExternalLink } from '@/components/Elements/index.js'
import { getIndexColumnDef } from '@/components/Elements/Table/Columns'
import { FEATURE_HEARING, HEARING_URL, SF_ACCOUNT_URL } from '@/config'
import { useDashboardDispatch } from '@/features/dashboard'
import { useGetContactHistoryQuery } from '@/features/job-proposals'

const useConfirm = ({ sfId, next }) => {
    const navigate = useNavigate()
    const dispatch = useDashboardDispatch()
    const current = useSelectedJobSeeker()
    return useCallback(() => {
        if (sfId === current?.sfId) {
            return navigate(next)
        }
        dispatch({ type: 'CONFIRM', payload: { sfId, next } })
    }, [dispatch, current, sfId, next])
}

export const Indicator = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const { data } = useGetContactHistoryQuery(rowSFID)
    if (data?.unreadNotification) {
        return <NewReactionBadge />
    }
}
Indicator.propTypes = {
    row: PropTypes.any,
}

export const FacilityCount = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const { data } = useGetContactHistoryQuery(rowSFID)
    if (data) {
        return `${data.proposed.facilityTotal}件`
    }
}
FacilityCount.propTypes = {
    row: PropTypes.any,
}
export const OfferCount = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const { data } = useGetContactHistoryQuery(rowSFID)
    if (data) {
        return `${data.proposed.offerTotal}件`
    }
}
FacilityCount.propTypes = {
    row: PropTypes.any,
}

export const ReferralStatus = (props) => {
    const data = useJobSeekerInfo(props)
    const str = data?.referralStatus?.replace(/^\d+\./, '') ?? ''
    switch (str) {
        case 'ファーストヒアリング':
            return 'FH'
        case 'セカンドヒアリング':
            return 'SH'
        default:
            return str
    }
}

export const DistributedAt = ({ value }) => {
    if (value) {
        return (
            <Text as="span" color="tryt.gray-4">
                {format(parseISO(value), 'yyyy/MM/dd')}
            </Text>
        )
    }
}
DistributedAt.propTypes = {
    value: PropTypes.string,
}

export const ManualImportedAt = ({ value }) => {
    if (value) {
        return (
            <Text as="span" color="tryt.gray-4">
                {format(parseISO(value), 'yyyy/MM/dd')}
            </Text>
        )
    }
}
ManualImportedAt.propTypes = {
    value: PropTypes.string,
}

export const Name = (props) => {
    const data = useJobSeekerInfo(props)
    const name = data?.name
    const hasCode = !!data?.code
    const { displayName, truncated } = name
        ? getDisplayNameByLength(name, 14)
        : { displayName: '', truncated: false }
    return (
        <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            width="100%"
            gap="3px"
        >
            <IconButton
                icon={<FacilitySFURLIcon color="#009ddb" boxSize="20px" />}
                borderColor="#EAEEF4"
                h="20px"
                w="20px"
                minW={0}
                minH={0}
                boxShadow="none"
                isDisabled={!hasCode}
                {...(hasCode && {
                    as: ExternalLink,
                    href: SF_ACCOUNT_URL + '/' + data.code + '/view',
                })}
                aria-label="Salesforce アカウントを開く"
            />
            {name &&
                (truncated ? (
                    <Tooltip label={name} hasArrow placement="bottom">
                        <Text
                            as="span"
                            width="8em"
                            flexShrink={0}
                            whiteSpace="normal"
                            wordBreak="break-all"
                            fontWeight="bold"
                            cursor="help"
                        >
                            {displayName}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text
                        as="span"
                        flexShrink={0}
                        whiteSpace="normal"
                        wordBreak="break-all"
                        fontWeight="bold"
                    >
                        {displayName}
                    </Text>
                ))}
            <Text as="span" flexShrink={0}>
                様
            </Text>
        </Box>
    )
}

export const Age = (props) => {
    const data = useJobSeekerInfo(props)
    if (data?.age) {
        return `${data.age}歳`
    }
}

export const Address = (props) => {
    const data = useJobSeekerInfo(props)
    return data?.city
}

export const Qualification = (props) => {
    const data = useJobSeekerInfo(props)
    return data?.qualifications
}

export const Phone = (props) => {
    const data = useJobSeekerInfo(props)
    return data?.phone
}

const makeHearingContactUrl = (sfId) =>
    sfId ? HEARING_URL + '/contact/' + sfId + '/edit' : '#'

export const NewDevelopmentLink = (props) => {
    const data = useJobSeekerInfo(props)
    const url = makeHearingContactUrl(data?.sfId)
    return (
        <IconButton
            as={Link}
            aria-label="hearing"
            icon={<HearingIcon boxSize="28px" />}
            rounded="md"
            variant="ghost"
            border="none"
            width="28px"
            height="28px"
            minW={0}
            minH={0}
            pt={2}
            boxShadow="none"
            _hover={{
                bg: '#EAEEFA',
            }}
            _active={{
                bg: '#EAEEFA',
            }}
            href={url}
            target="_blank"
        />
    )
}
NewDevelopmentLink.propTypes = {
    row: PropTypes.any,
}

export const Search = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const { data } = useGetContactHistoryQuery(rowSFID)
    const recommendation = useConfirm({
        sfId: rowSFID,
        next: `/job-seekers/${rowSFID}/recommendations`,
    })
    const entry = useConfirm({
        sfId: rowSFID,
        next: `/job-seekers/${rowSFID}/entry`,
    })
    return (
        <ButtonGroup spacing="4px">
            <ActionButton
                fontSize="12px"
                isDisabled={!data?.proposed?.facilityTotal}
                onClick={recommendation}
            >
                AI
            </ActionButton>
            <ActionButton fontSize="12px" onClick={entry}>
                手動
            </ActionButton>
        </ButtonGroup>
    )
}
Search.propTypes = {
    row: PropTypes.any,
}

export const Delete = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const rowDistributedAt = props.row.original.distributedAt
    const [deleteClient] = useDeleteManualWatchListMutation()
    return (
        <DeleteButton
            onClick={() => {
                deleteClient({
                    sfId: rowSFID,
                    date: rowDistributedAt,
                })
            }}
        />
    )
}
Delete.propTypes = {
    row: PropTypes.any,
}

export const Contact = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const contact = useConfirm({
        sfId: rowSFID,
        next: `/job-seekers/${rowSFID}`,
    })
    return (
        <ActionButton fontSize="xs" variant="link" onClick={contact}>
            対応履歴&gt;
        </ActionButton>
    )
}
Contact.propTypes = {
    row: PropTypes.any,
}

export const RadioSFID = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    // const contact = useConfirm({
    //     sfId: rowSFID,
    //     next: `/job-seekers/${rowSFID}`,
    // })
    return (
        // <ActionButton fontSize="xs" variant="link" onClick={contact}>
        //     対応履歴&gt;
        // </ActionButton>
        //<input type="radio" name="productSelection" value="Laptop">
        <Radio value={rowSFID} />
    )
}
RadioSFID.propTypes = {
    row: PropTypes.any,
}

export const Done = (props) => {
    const rowSFID = props.row.original.jobseekerSfId
    const dispatch = useDashboardDispatch()
    return (
        <Box>
            <DoneButton
                onClick={() => {
                    dispatch({
                        type: 'DONE',
                        payload: { sfId: rowSFID, next: 'done' },
                    })
                }}
                aria-label="done"
                icon={<CgSoftwareDownload />}
            />
        </Box>
    )
}
Done.propTypes = {
    row: PropTypes.any,
}

const ActionButton = forwardRef((props, ref) => {
    return (
        <Button
            ref={ref}
            variant="gradient"
            h="21px"
            minW={0}
            w="38px"
            py={0}
            {...props}
        />
    )
})

const DeleteButton = forwardRef((props, ref) => {
    return (
        <IconButton
            ref={ref}
            aria-label="削除"
            icon={
                <Box
                    w="16px"
                    h="16px"
                    bg="red.500"
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontSize="12px"
                    fontWeight="bold"
                >
                    −
                </Box>
            }
            variant="ghost"
            size="sm"
            {...props}
        />
    )
})

/**
 * @param {any} props
 * @returns {any} Data
 */
const useJobSeekerInfo = (props) => {
    const { data } = useGetMergedJobSeekerQuery(
        props.row.original.jobseekerSfId
    )
    return data
}

const DoneButton = forwardRef(
    (
        /** @type {import('@chakra-ui/react').IconButtonProps} */
        props,
        ref
    ) => {
        return (
            <IconButton
                ref={ref}
                variant="ghost"
                rounded="none"
                // color="tryt.primary-1"
                // display="inline-flex"
                {...props}
            />
        )
    }
)

const CssIndex = () => {
    // NOTE: ソート時の行番号もってなさそうだったので苦肉の策。。。
    // https://developer.mozilla.org/ja/docs/Web/CSS/CSS_counter_styles/Using_CSS_counters
    return (
        <Box
            sx={{
                _before: {
                    counterIncrement: 'row-number',
                    content: 'counter(row-number)',
                },
            }}
        />
    )
}

export const defaultListColumns = [
    getIndexColumnDef((option) => ({ ...option, Cell: CssIndex })),
    {
        id: 'indicator',
        Header: '新着',
        Cell: Indicator,
        width: '36px',
        className: 'td-center',
    },
    {
        id: 'distributedAt',
        accessor: 'distributedAt',
        Header: '配布日',
        Cell: DistributedAt,
        width: '75px',
    },
    {
        id: 'manualImportedAt',
        accessor: 'distributedAt',
        Header: '追加日',
        Cell: ManualImportedAt,
        width: '75px',
    },
    {
        id: 'referralStatus',
        Header: '紹介ステータス',
        Cell: ReferralStatus,
        width: '100px',
    },
    // {
    //     accessor: 'jobseekerSfId',
    //     Header: '候補者SFID',
    //     width: '82px',
    //     disableSortBy: true,
    // },
    {
        id: 'name',
        Header: '候補者名',
        Cell: Name,
        width: '150px',
    },
    {
        id: 'age',
        Header: '年齢',
        Cell: Age,
        width: '35px',
        className: 'td-center',
    },
    {
        id: 'address',
        Header: '住所',
        Cell: Address,
    },
    {
        id: 'qualification',
        Header: '優先資格',
        Cell: Qualification,
    },
    {
        id: 'phone',
        Header: '電話番号',
        Cell: Phone,
        width: '110px',
    },
    {
        id: 'facilityCount',
        Header: '提案施設',
        Cell: FacilityCount,
        width: '62px',
        className: 'td-center',
    },
    {
        id: 'offerCount',
        Header: '提案求人',
        Cell: OfferCount,
        width: '62px',
        className: 'td-center',
    },
    {
        id: 'search',
        Header: '施設検索',
        Cell: Search,
        width: '88px',
        className: 'td-center',
    },
    {
        id: 'delete',
        Header: '削除',
        Cell: Delete,
        width: '40px',
        className: 'td-center',
    },
    {
        id: 'contact',
        Cell: Contact,
        className: 'td-center',
        width: '75px',
    },
    FEATURE_HEARING && {
        id: 'newDevelopment',
        Header: '記録',
        Cell: NewDevelopmentLink,
        width: '80px',
        className: 'td-center',
    },
].filter((column) => column)

export const ModalsWatchListColumns = [
    getIndexColumnDef((option) => ({ ...option, Cell: CssIndex })),
    {
        accessor: 'distributedAt',
        Header: '配布日',
        Cell: DistributedAt,
        width: '75px',
    },
    {
        id: 'referralStatus',
        Header: '紹介ステータス',
        Cell: ReferralStatus,
        width: '100px',
    },
    // {
    //     accessor: 'jobseekerSfId',
    //     Header: '候補者SFID',
    //     width: '82px',
    //     disableSortBy: true,
    // },
    {
        id: 'name',
        Header: '候補者名',
        Cell: Name,
        width: '150px',
    },
    {
        id: 'age',
        Header: '年齢',
        Cell: Age,
        width: '35px',
        className: 'td-center',
    },
    {
        id: 'address',
        Header: '住所',
        Cell: Address,
    },
    {
        id: 'qualification',
        Header: '優先資格',
        Cell: Qualification,
    },
    {
        id: 'phone',
        Header: '電話番号',
        Cell: Phone,
        width: '110px',
    },
    {
        id: 'radio',
        Header: '選択',
        Cell: RadioSFID,
        className: 'td-center',
        width: '40px',
    },
    {
        id: 'newDevelopment',
        Header: '記録',
        Cell: NewDevelopmentLink,
        width: '80px',
        className: 'td-center',
    },
]

export const watchListColumns = [].concat(
    defaultListColumns.filter(
        ({ id }) => id !== 'manualImportedAt' && id !== 'delete'
    )
)
export const manualWatchListColumns = [getIndexColumnDef()].concat(
    defaultListColumns.filter(
        ({ id }) => id !== 'index' && id !== 'distributedAt'
    )
)
export const doneListColumns = [getIndexColumnDef()].concat(
    watchListColumns.filter(
        ({ id }) => id !== 'index' && id !== 'search' && id !== 'newDevelopment'
    )
)

//候補者未選択時にTOP画面のような表をモーダルで表示する用
export const ModalsDoneListColumns = [getIndexColumnDef()].concat(
    ModalsWatchListColumns.filter(({ id }) => id !== 'search' && id !== 'index')
)
